import { RbacAction, RbacModuleKey } from '../services/rbac.service';
import { MemberRoleEnum } from 'src/enumerators';

/**
 * Permissões de contingência usadas quando os endpoints de RBAC não estão
 * disponíveis (ver `RbacService.refreshMe`). NÃO é uma segunda fonte de verdade:
 * enquanto `/rbac/me` responde, ele é a única origem das permissões.
 *
 * O objetivo é manter o software utilizável num cenário de indisponibilidade —
 * sem esta tabela, `can()` devolveria `false` para tudo e a aplicação viraria
 * uma casca vazia, com todos os menus e ações escondidos.
 *
 * A autorização de verdade continua no backend: estas permissões só governam o
 * que a UI mostra. Uma requisição que o backend recuse segue sendo recusada.
 *
 * ---------------------------------------------------------------------------
 * ORIGEM DOS DADOS — este arquivo é um espelho, gerado a partir do backend:
 *
 *   backend/src/database/rbac/rbac.policies.js   (POLICIES_BY_PROFILE)
 *   backend/src/database/rbac/rbac.modules.js    (catálogo de módulos/ações)
 *   backend/src/database/rbac/full-only-modules.js
 *
 * O mapeamento role -> perfil de sistema vem do backfill oficial
 * (backend/src/database/seeders/20251218000100-backfill-rbac.prod.js:277-280):
 *
 *   MANAGER     -> SYSTEM:OWNER
 *   OPERATOR    -> SYSTEM:ADMIN
 *   TEAM_LEADER -> SYSTEM:PARTICIPANT
 *   (desconhecido) -> SYSTEM:PARTICIPANT, menor privilégio
 *
 * Ao mexer nas políticas do backend, regenere este arquivo. A divergência é
 * inofensiva para segurança (o backend enforça), mas causa menu a mais ou a
 * menos durante a contingência.
 *
 * ---------------------------------------------------------------------------
 * LIMITE ESTRUTURAL — regenerar este arquivo NÃO resolve tudo:
 *
 * A contingência mapeia role -> perfil de SISTEMA (`fallbackSystemProfileFor`) e
 * ignora o perfil efetivamente atribuído ao membro. Perfil customizado é dado do
 * workspace, não do repositório — não há espelho versionado que o alcance.
 *
 * Ou seja, para todo membro cujo perfil não seja exatamente o de sistema do seu
 * role, a contingência erra por construção, e nos dois sentidos: perfil
 * restritivo vê menu a mais (com 403 do backend a cada clique), perfil ampliado
 * perde itens. Isso vale mesmo com o espelho perfeitamente atualizado.
 *
 * É por isso que `RbacService.refreshMe` só aplica esta tabela quando NÃO há
 * resposta real carregada: a resposta real, ainda que vencida pelo TTL, conhece
 * o perfil customizado — esta tabela não tem como conhecer.
 * ---------------------------------------------------------------------------
 */

export type RbacPermissionMap = Record<RbacModuleKey, RbacAction[]>;

/**
 * Catálogo de módulos e ações (espelho de rbac.modules.js). O perfil
 * Proprietário é derivado daqui — assim como no backend, onde OWNER_POLICIES é
 * gerado do catálogo justamente para não ficar defasado.
 */
export const RBAC_MODULE_CATALOG: RbacPermissionMap = {
  'organization': ['read', 'update'],
  'contracts': ['create', 'read', 'update', 'delete'],
  'contracted_companies': ['create', 'read', 'update', 'delete'],
  'user_invites': ['create', 'read', 'update', 'delete'],
  'map': ['read'],
  'catalog': ['create', 'read', 'update', 'delete'],
  'pendencies': ['create', 'read', 'update', 'delete'],
  'signaling_history': ['read'],
  'occurrences': ['create', 'read', 'update', 'delete'],
  'projects': ['create', 'read', 'update', 'delete'],
  'regions': ['create', 'read', 'update', 'delete'],
  'areas': ['create', 'read', 'update', 'delete'],
  'routes': ['create', 'read', 'update', 'delete'],
  'paths': ['create', 'read', 'update', 'delete'],
  'stock': ['create', 'read', 'update', 'delete'],
  'services': ['create', 'read', 'update', 'delete'],
  'service_pendencies': ['read'],
  'service_order': ['create', 'read', 'update', 'delete'],
  'service_order_adjustments': ['create', 'read', 'update', 'delete'],
  'tasks': ['create', 'read', 'update', 'delete'],
  'task_management_view': ['read'],
  'inspection_screen': ['create', 'read', 'update', 'delete'],
  'teams': ['create', 'read', 'update', 'delete'],
  'team_tracking': ['read'],
  'vehicles': ['create', 'read', 'update', 'delete'],
  'reports': ['create', 'read', 'update', 'delete'],
  'dashboard': ['read'],
  'financial': ['read'],
  'report_settings': ['create', 'read', 'update', 'delete'],
  'dimensions': ['create', 'read', 'update', 'delete'],
  'pending_tasks_settings': ['create', 'read', 'update', 'delete'],
  'offline_maps': ['create', 'read', 'update', 'delete'],
  'app_notifications': ['create', 'read'],
  'notifications': ['create', 'read'],
  'sharing_groups': ['create', 'read', 'update', 'delete'],
  'ombudsman_settings': ['create', 'read', 'update'],
  'sla_rules': ['create', 'read', 'update', 'delete'],
  'warranty': ['create', 'read', 'update', 'delete'],
  'warranty_notifications': ['read'],
  'inspections': ['create', 'read', 'update', 'delete'],
  'profiles': ['create', 'read', 'update', 'delete'],
  'members': ['create', 'read', 'update', 'delete'],
  'member_removal': ['read', 'delete'],
  'ownership_transfer': ['update'],
};

/**
 * Módulos exclusivos da licença FULL (espelho de full-only-modules.js).
 * Organizações LIGHT não os recebem — mesma regra de isModuleAllowedForLicense.
 */
export const FULL_ONLY_MODULES: ReadonlySet<RbacModuleKey> = new Set([
  'teams',
  'team_tracking',
  'tasks',
  'task_management_view',
  'service_order',
  'service_order_adjustments',
  'service_pendencies',
  'vehicles',
]);

/** Permissões do perfil Administrador (SYSTEM:ADMIN). */
const ADMIN_PERMISSIONS: RbacPermissionMap = {
  'organization': ['read', 'update'],
  'contracts': ['create', 'read', 'update', 'delete'],
  'contracted_companies': ['create', 'read', 'update', 'delete'],
  'user_invites': ['create', 'read', 'delete'],
  'map': ['read'],
  'catalog': ['create', 'read', 'update', 'delete'],
  'pendencies': ['create', 'read', 'update', 'delete'],
  'signaling_history': ['read'],
  'occurrences': ['create', 'read', 'update', 'delete'],
  'projects': ['create', 'read', 'update', 'delete'],
  'regions': ['create', 'read', 'update', 'delete'],
  'areas': ['create', 'read', 'update', 'delete'],
  'routes': ['create', 'read', 'update', 'delete'],
  'paths': ['create', 'read', 'update', 'delete'],
  'stock': ['create', 'read', 'update', 'delete'],
  'services': ['create', 'read', 'update', 'delete'],
  'service_pendencies': ['read'],
  'service_order': ['create', 'read', 'update', 'delete'],
  'service_order_adjustments': ['create', 'read', 'update'],
  'tasks': ['create', 'read', 'update', 'delete'],
  'task_management_view': ['read'],
  'inspection_screen': ['read'],
  'teams': ['create', 'read', 'update', 'delete'],
  'team_tracking': ['read'],
  'vehicles': ['create', 'read', 'update', 'delete'],
  'reports': ['create', 'read', 'delete'],
  'dashboard': ['read'],
  'financial': ['read'],
  'report_settings': ['create', 'read', 'update', 'delete'],
  'dimensions': ['create', 'read', 'update', 'delete'],
  'pending_tasks_settings': ['create', 'read', 'update', 'delete'],
  'offline_maps': ['create', 'read', 'update', 'delete'],
  'app_notifications': ['create', 'read'],
  'notifications': ['create', 'read'],
  'sharing_groups': ['create', 'read', 'update', 'delete'],
  'ombudsman_settings': ['create', 'read', 'update'],
  'sla_rules': ['create', 'read', 'update', 'delete'],
  'warranty': ['create', 'read', 'update', 'delete'],
  'inspections': ['create', 'read', 'update', 'delete'],
  'profiles': ['create', 'read', 'update', 'delete'],
  'members': ['create', 'read', 'update', 'delete'],
};

/** Permissões do perfil Participante (SYSTEM:PARTICIPANT). */
const PARTICIPANT_PERMISSIONS: RbacPermissionMap = {
  'contracts': ['read'],
  'map': ['read'],
  'catalog': ['create', 'read'],
  'pendencies': ['read'],
  'signaling_history': ['read'],
  'projects': ['read'],
  'regions': ['read'],
  'areas': ['read'],
  'routes': ['read'],
  'paths': ['read'],
  'stock': ['read'],
  'services': ['read'],
  'service_pendencies': ['read'],
  'service_order': ['read'],
  'tasks': ['read'],
  'inspections': ['read'],
};

/**
 * Proprietário (SYSTEM:OWNER) tem acesso total: todos os módulos e todas as
 * ações do catálogo. Derivado, nunca mantido à mão.
 */
const OWNER_PERMISSIONS: RbacPermissionMap = Object.entries(RBAC_MODULE_CATALOG).reduce(
  (acc, [moduleKey, actions]) => {
    acc[moduleKey] = [...actions];
    return acc;
  },
  {} as RbacPermissionMap,
);

const PERMISSIONS_BY_ROLE: Record<MemberRoleEnum, RbacPermissionMap> = {
  [MemberRoleEnum.Manager]: OWNER_PERMISSIONS,
  [MemberRoleEnum.Operator]: ADMIN_PERMISSIONS,
  [MemberRoleEnum.TeamLeader]: PARTICIPANT_PERMISSIONS,
};

/** Licença LIGHT — mesma string do LicenseTypeEnum do backend. */
const LIGHT_LICENSE = 'LIGHT';

/**
 * Monta as permissões de contingência para um role de membro, aplicando o
 * filtro de licença (LIGHT não recebe os módulos exclusivos de FULL).
 *
 * Role desconhecido cai em Participante — menor privilégio, mesmo fallback
 * defensivo do backfill do backend.
 */
export function buildFallbackPermissions(
  role: MemberRoleEnum | string | null | undefined,
  licenseType?: string | null,
): RbacPermissionMap {
  const base =
    PERMISSIONS_BY_ROLE[role as MemberRoleEnum] ?? PARTICIPANT_PERMISSIONS;

  if (licenseType !== LIGHT_LICENSE) return { ...base };

  return Object.entries(base).reduce((acc, [moduleKey, actions]) => {
    if (!FULL_ONLY_MODULES.has(moduleKey)) acc[moduleKey] = actions;
    return acc;
  }, {} as RbacPermissionMap);
}

/** O perfil de sistema equivalente ao role, usado por `RbacService.isOwner`. */
export function fallbackSystemProfileFor(role: MemberRoleEnum | string | null | undefined): string {
  if (role === MemberRoleEnum.Manager) return 'SYSTEM:OWNER';
  if (role === MemberRoleEnum.Operator) return 'SYSTEM:ADMIN';
  return 'SYSTEM:PARTICIPANT';
}
